import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../axios-config';
import { UserContext } from './UserContext';
import { useHallPassTypes } from './HallPassTypesContext';
import { HallPassStatuses } from '../types/enums';

type RangeKey = 'today' | 'yesterday' | 'last7' | 'last30';

interface HistoryFilter {
  range?: RangeKey | null;
  start?: Date | null;
  end?: Date | null; // inclusive
}

interface HallPassesContextValue {
  loading: boolean;
  activeHallPasses: any[];
  hallPassHistory: any[];
  historyFilter: HistoryFilter;
  applyHistoryFilter: (filter: HistoryFilter) => Promise<void>;
  refreshActive: () => Promise<void>;
  upsertFromHub: (hp: any) => void;
  removeFromHub: (hp: any) => void;
  lastUpdated: Date | null;
  autoRefreshEnabled: boolean;
  autoRefreshInterval: number;
  timeUntilNextRefresh: number;
  setAutoRefreshEnabled: (enabled: boolean) => void;
  setAutoRefreshInterval: (interval: number) => void;
  forceRefresh: () => Promise<void>;
  currentTab: string;
  // Use React state setter signature to allow functional updates
  setCurrentTab: React.Dispatch<React.SetStateAction<'active' | 'history' | 'map' | 'types' | 'userHallPassTypes' | 'incidents' | 'students'>>;
}

const HallPassesContext = createContext<HallPassesContextValue>({
  loading: false,
  activeHallPasses: [],
  hallPassHistory: [],
  historyFilter: { range: 'today' },
  applyHistoryFilter: async () => { },
  refreshActive: async () => { },
  upsertFromHub: () => { },
  removeFromHub: () => { },
  lastUpdated: null,
  autoRefreshEnabled: true,
  autoRefreshInterval: 30000,
  timeUntilNextRefresh: 0,
  setAutoRefreshEnabled: () => { },
  setAutoRefreshInterval: () => { },
  forceRefresh: async () => { },
  currentTab: 'active',
  setCurrentTab: () => { },
});

// -------------------- helpers --------------------
const getUnifiedKey = (p: any) => {
  const primary = p?.hallPassId ?? p?.id ?? p?.passId;
  if (primary) return primary.toString();
  const start = p?.startTime ? new Date(p.startTime) : new Date();
  const minuteBucket = isNaN(start.getTime()) ? Date.now() : Math.floor(start.getTime() / 60000);
  return `temp-${p?.studentId || 'stu'}-${p?.hallPassTypeId || 'type'}-${minuteBucket}`;
};

const getPassKey = (p: any) => getUnifiedKey(p);

const upsert = (existing: any[], incoming: any[]): any[] => {
  const map = new Map<string, any>();
  existing.forEach(p => map.set(getPassKey(p), p));
  incoming.forEach(p => map.set(getPassKey(p), p));
  return Array.from(map.values());
};

const normalizeHallPass = (hp: any, hallPassTypes: any[]): any => {
  if (!hp) return hp;
  const matchingType = hallPassTypes.find(t => t.hallPassTypeId === hp.hallPassTypeId);
  const rawStart = hp.startTime || hp.requestDecisionedAt || hp.requestAt || hp.createdAt;
  const dt = rawStart ? new Date(rawStart) : new Date();
  const startTime = isNaN(dt.getTime()) ? new Date().toISOString() : dt.toISOString();

  const rawEnd = hp.returnAt || hp.endTime || hp.actualReturnTime || hp.expiredAt || hp.cancelledAt;
  let endTime: string | undefined = undefined;
  if (rawEnd) {
    const endDt = new Date(rawEnd);
    if (!isNaN(endDt.getTime())) endTime = endDt.toISOString();
  }

  const candidates: number[] = [];
  const declaredDuration = Number(hp.durationMinutes ?? hp.duration);
  if (!isNaN(declaredDuration) && declaredDuration > 0) candidates.push(declaredDuration);
  const typeDuration = Number(matchingType?.hallPassTypeDuration);
  if (!isNaN(typeDuration) && typeDuration > 0) candidates.push(typeDuration);
  if (hp.expectedReturnTime) {
    const endDt = new Date(hp.expectedReturnTime);
    const diffMin = Math.round((endDt.getTime() - dt.getTime()) / 60000);
    if (!isNaN(diffMin) && diffMin > 0) candidates.push(diffMin);
  }
  candidates.push(5);
  const sane = Array.from(new Set(candidates.filter(c => c > 0 && c <= 480))).sort((a, b) => a - b);
  const duration = Math.max(1, sane[0] || 5);
  const location = hp.location || matchingType?.hallPassTypeDescription || hp.destinationLocationName || '';
  const id = hp.hallPassId || hp.id || hp.passId;

  return {
    ...hp,
    hallPassId: id,
    id,
    startTime,
    endTime: endTime || hp._computedEndTime,
    duration,
    location,
    hallPassTypeName: hp.hallPassTypeName || matchingType?.hallPassTypeName || matchingType?.hallPassTypeDescription,
    hallPassTypeDescription: hp.hallPassTypeDescription || matchingType?.hallPassTypeDescription || matchingType?.hallPassTypeName,
  };
};

const isHallPassRelevant = (hp: any, ctx: { userProfile: any; students: any[] }) => {
  if (!hp) return false;
  const { userProfile, students } = ctx;
  if (userProfile?.orgId && hp.organizationId && hp.organizationId !== userProfile.orgId) return false;
  const role = userProfile?.mainRole || '';
  if (/SECURITY|ADMIN|PRINCIPAL|DEAN|COUNSELOR/.test(role)) return true;
  if (role.includes('TEACHER')) {
    if (hp.teacherId && hp.teacherId === userProfile?.userId) return true;
    if (students?.some(s => s.userId === hp.studentId)) return true;
    return false;
  }
  if (role.includes('STUDENT')) {
    return hp.studentId === userProfile?.userId;
  }
  return true;
};

// ----------------------------- provider -----------------------------
export const HallPassesProvider = ({ children }: { children: React.ReactNode }) => {
  const { userProfile } = useContext(UserContext);
  const students = userProfile?.students || [];
  const { hallPassTypes } = useHallPassTypes();
  const location = useLocation();

  // Explicit allow/deny logic for hall pass auto-refresh scope
  const isHallPassRoute = useMemo(() => {
    const p = location.pathname.toLowerCase();
    const deny = [
      '/hallpass/types',
      '/hallpass/type',
      '/user-hallpass-types',
      '/hallpass-type',
    ];
    if (deny.some(d => p.startsWith(d))) return false;
    const allow = [
      '/hallpass',
      '/hallpass/',
      '/hallpass/map',
      '/student/hallpass',
      '/student/hallpass/map',
      '/teacher/hallpass',
      '/teacher/hallpass/map',
    ];
    return allow.some(a => p.startsWith(a));
  }, [location.pathname]);

  const [loading, setLoading] = useState(false);
  const [activeHallPasses, setActiveHallPasses] = useState<any[]>([]);
  const [hallPassHistory, setHallPassHistory] = useState<any[]>([]);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>({ range: 'today' });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('hallpass-auto-refresh-enabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('hallpass-auto-refresh-interval');
    return saved !== null ? parseInt(saved, 10) : 30000;
  });
  const [timeUntilNextRefresh, setTimeUntilNextRefresh] = useState(0);

  // Tab state drives auto-refresh gating
  const [currentTab, setCurrentTab] = useState<'active' | 'history' | 'map' | 'types' | 'userHallPassTypes' | 'incidents' | 'students'>('active');

  const initializedRef = useRef(false);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTimeRef = useRef<Date>(new Date());

  const normalizeAll = useCallback((arr: any[]): any[] => arr.map(hp => normalizeHallPass(hp, hallPassTypes)), [hallPassTypes]);

  const splitActiveHistory = useCallback((all: any[]) => {
    const normalized = normalizeAll(all);
    const active = normalized.filter(hp => hp.hallPassStatusId === HallPassStatuses.Active && isHallPassRelevant(hp, { userProfile, students }));
    let historyBase = normalized.filter(hp => hp.hallPassStatusId !== HallPassStatuses.Pending && hp.hallPassStatusId !== HallPassStatuses.Active);
    const role = userProfile?.mainRole || '';
    if (role.includes('STUDENT')) {
      historyBase = historyBase.filter(hp => isHallPassRelevant(hp, { userProfile, students }));
    }
    return { active, historyBase };
  }, [normalizeAll, userProfile, students]);

  const refreshActive = useCallback(async () => {
    if (!isHallPassRoute) {
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get('/api/HallPasses');
      const { active, historyBase } = splitActiveHistory(data || []);
      setActiveHallPasses(active);
      setHallPassHistory(historyBase);
      const now = new Date();
      setLastUpdated(now);
      lastRefreshTimeRef.current = now;
    } finally {
      setLoading(false);
    }
  }, [splitActiveHistory, isHallPassRoute]);

  const forceRefresh = useCallback(async () => {
    await refreshActive();
    startCountdownTimer();
  }, [refreshActive]);

  const isPageVisible = useCallback(() => document.visibilityState === 'visible', []);

  // Countdown
  const startCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    const shouldShowCountdown = autoRefreshEnabled && isHallPassRoute && (currentTab === 'active' || currentTab === 'map');
    if (!shouldShowCountdown) {
      setTimeUntilNextRefresh(0);
      return;
    }

    const updateCountdown = () => {
      const elapsed = Date.now() - lastRefreshTimeRef.current.getTime();
      const remaining = Math.max(0, autoRefreshInterval - elapsed);
      setTimeUntilNextRefresh(remaining);

      if (remaining === 0) {
        lastRefreshTimeRef.current = new Date();
        setTimeUntilNextRefresh(autoRefreshInterval);
      }
    };

    countdownTimerRef.current = setInterval(updateCountdown, 1000);
    updateCountdown();
  }, [autoRefreshEnabled, autoRefreshInterval, currentTab, isHallPassRoute]);

  // Auto-refresh
  const setupAutoRefresh = useCallback(() => {
    // Clear any previous intervals up front
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    const shouldAutoRefresh =
      autoRefreshEnabled &&
      isHallPassRoute &&
      (currentTab === 'active' || currentTab === 'map');

    const startAuto = () => {
      autoRefreshTimerRef.current = setInterval(() => {
        if (isPageVisible()) {
          refreshActive(); // Ensure refreshActive is called
        }
      }, autoRefreshInterval);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (shouldAutoRefresh && !autoRefreshTimerRef.current) {
          startAuto();
          startCountdownTimer();
        }
      } else if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
    };

    if (!shouldAutoRefresh) {
      setTimeUntilNextRefresh(0);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
        if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
        autoRefreshTimerRef.current = null;
        countdownTimerRef.current = null;
      };
    }

    startAuto();
    startCountdownTimer();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      autoRefreshTimerRef.current = null;
      countdownTimerRef.current = null;
    };
  }, [autoRefreshEnabled, autoRefreshInterval, currentTab, refreshActive, isPageVisible, startCountdownTimer, isHallPassRoute]);

  useEffect(() => {
    const cleanup = setupAutoRefresh();
    try { console.log('[HallPassesContext] effect run for auto-refresh, path=', location.pathname); } catch { }
    return cleanup;
  }, [setupAutoRefresh]);

  useEffect(() => {
    localStorage.setItem('hallpass-auto-refresh-enabled', JSON.stringify(autoRefreshEnabled));
  }, [autoRefreshEnabled]);

  useEffect(() => {
    localStorage.setItem('hallpass-auto-refresh-interval', autoRefreshInterval.toString());
  }, [autoRefreshInterval]);

  const handleSetAutoRefreshEnabled = useCallback((enabled: boolean) => {
    setAutoRefreshEnabled(enabled);
    if (!enabled) {
      if (autoRefreshTimerRef.current) { clearInterval(autoRefreshTimerRef.current); autoRefreshTimerRef.current = null; }
      if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
      setTimeUntilNextRefresh(0);
    } else {
      // Reset last refresh time so countdown restarts cleanly when re-enabled
      lastRefreshTimeRef.current = new Date();
    }
  }, []);

  // Sync currentTab with route navigation for hallpass/map views
  useEffect(() => {
    const p = location.pathname.toLowerCase();
    if (p.includes('/hallpass/map')) {
      setCurrentTab('map');
    } else if (p.includes('hallpass') && !p.includes('type')) {
      setCurrentTab(prev => (prev === 'map' ? 'map' : 'active'));
    }
  }, [location.pathname]);

  const handleSetAutoRefreshInterval = useCallback((interval: number) => {
    setAutoRefreshInterval(interval);
    lastRefreshTimeRef.current = new Date();
  }, []);

  useEffect(() => {
    if (!initializedRef.current && hallPassTypes.length > 0) {
      initializedRef.current = true;
      refreshActive();
    }
  }, [hallPassTypes, refreshActive]);

  const applyHistoryFilter = useCallback(async (filter: HistoryFilter) => {
    setHistoryFilter(filter);
    const params: any = {};
    if (filter.range) params.range = filter.range;
    if (filter.start) {
      const d = filter.start;
      params.start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (filter.end) {
      const d = filter.end;
      params.end = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    setLoading(true);
    try {
      const { data } = await axios.get('/api/HallPasses', { params });
      const normalized = normalizeAll(data || []);
      const role = userProfile?.mainRole || '';
      let historyOnly = normalized.filter(hp => hp.hallPassStatusId !== HallPassStatuses.Pending && hp.hallPassStatusId !== HallPassStatuses.Active);
      if (role.includes('STUDENT')) {
        historyOnly = historyOnly.filter(hp => isHallPassRelevant(hp, { userProfile, students }));
      }
      historyOnly.sort((a, b) => new Date(b.startTime || b.createdAt || 0).getTime() - new Date(a.startTime || a.createdAt || 0).getTime());
      setHallPassHistory(historyOnly);
    } finally {
      setLoading(false);
    }
  }, [normalizeAll, userProfile, students]);

  const upsertFromHub = useCallback((hp: any) => {
    const normalized = normalizeHallPass(hp, hallPassTypes);
    const statusLower = (hp.status || '').toString().toLowerCase();
    const isPastLike = ['returned', 'expired', 'cancelled'].includes(statusLower);
    const isActiveLike =
      !isPastLike &&
      (['summons', 'pending'].includes(statusLower) ||
        hp.hallPassStatusId === HallPassStatuses.Pending ||
        hp.hallPassStatusId === HallPassStatuses.Active);

    if (isPastLike) {
      setActiveHallPasses(prev => prev.filter(p => getPassKey(p) !== getPassKey(normalized)));
      setHallPassHistory(prev => {
        const role = userProfile?.mainRole || '';
        if (role.includes('STUDENT') && !isHallPassRelevant(normalized, { userProfile, students })) return prev;
        const merged = upsert(prev, [normalized]);
        return merged.sort((a, b) => new Date(b.startTime || b.createdAt || 0).getTime() - new Date(a.startTime || a.createdAt || 0).getTime());
      });
      const now = new Date();
      setLastUpdated(now);
      lastRefreshTimeRef.current = now;
      return;
    }

    if (isActiveLike) {
      if (!isHallPassRelevant(normalized, { userProfile, students })) return;
      setActiveHallPasses(prev => upsert(prev, [normalized])); // Defensive check added
      const now = new Date();
      setLastUpdated(now);
      lastRefreshTimeRef.current = now;
      return;
    }

    if (!hp.status && isHallPassRelevant(normalized, { userProfile, students })) {
      setActiveHallPasses(prev => upsert(prev, [normalized]));
      const now = new Date();
      setLastUpdated(now);
      lastRefreshTimeRef.current = now;
    }
  }, [hallPassTypes, userProfile, students]);

  const removeFromHub = useCallback((hp: any) => {
    const normalized = normalizeHallPass(hp, hallPassTypes);
    setActiveHallPasses(prev => prev.filter(p => getPassKey(p) !== getPassKey(normalized)));
    const now = new Date();
    setLastUpdated(now);
    lastRefreshTimeRef.current = now;
  }, [hallPassTypes]);

  const value = useMemo(() => ({
    loading,
    activeHallPasses,
    hallPassHistory,
    historyFilter,
    applyHistoryFilter,
    refreshActive,
    upsertFromHub,
    removeFromHub,
    lastUpdated,
    autoRefreshEnabled,
    autoRefreshInterval,
    timeUntilNextRefresh,
    setAutoRefreshEnabled: handleSetAutoRefreshEnabled,
    setAutoRefreshInterval: handleSetAutoRefreshInterval,
    forceRefresh,
    currentTab,
    setCurrentTab,
  }), [
    loading,
    activeHallPasses,
    hallPassHistory,
    historyFilter,
    applyHistoryFilter,
    refreshActive,
    upsertFromHub,
    removeFromHub,
    lastUpdated,
    autoRefreshEnabled,
    autoRefreshInterval,
    timeUntilNextRefresh,
    handleSetAutoRefreshEnabled,
    handleSetAutoRefreshInterval,
    forceRefresh,
    currentTab,
  ]);

  return (
    <HallPassesContext.Provider value={value}>
      {children}
    </HallPassesContext.Provider>
  );
};

export const useHallPasses = () => useContext(HallPassesContext);
